import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTDOWN_SEC = 60;                   // seconds of warning before logout
const REFRESH_THROTTLE_MS = 10 * 60 * 1000; // slide the session at most every 10 min while active

/**
 * Optional idle auto sign-out. Controlled per-org via Admin → Settings
 * (idle_timeout_enabled / idle_timeout_minutes). OFF by default — when off this
 * renders nothing and never logs anyone out. When on, warns before signing out.
 * Test overrides: window.__IDLE_WARN_MS__ (ms until warning) and
 * window.__SESSION_COUNTDOWN_SEC__ (countdown seconds) force it on for verification.
 */
export const SessionTimeout = () => {
  const { user, logout } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [idleMs, setIdleMs] = useState(30 * 60 * 1000);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SEC);
  const lastActivityRef = useRef(Date.now());
  const lastRefreshRef = useRef(Date.now());
  const showWarningRef = useRef(false);

  const testOverrideMs = () => Number(typeof window !== 'undefined' && window.__IDLE_WARN_MS__) || 0;

  // Load the org idle-timeout policy
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      const override = testOverrideMs();
      try {
        const res = await axios.get(`${API}/auth/session-policy`);
        if (cancelled) return;
        if (override) {
          setEnabled(true);
          setIdleMs(override);
        } else {
          setEnabled(Boolean(res.data?.idle_timeout_enabled));
          setIdleMs(Math.max(Number(res.data?.idle_timeout_minutes) || 30, 1) * 60 * 1000);
        }
      } catch (e) {
        if (!cancelled && override) { setEnabled(true); setIdleMs(override); }
        // otherwise stay disabled (safe default)
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const refreshSession = useCallback(async () => {
    try { await axios.post(`${API}/auth/refresh`); lastRefreshRef.current = Date.now(); } catch (e) { /* 401 handled by interceptor */ }
  }, []);

  const onActivity = useCallback(() => {
    if (showWarningRef.current) return;
    lastActivityRef.current = Date.now();
    if (Date.now() - lastRefreshRef.current > REFRESH_THROTTLE_MS) refreshSession();
  }, [refreshSession]);

  // Activity listeners (only while enabled)
  useEffect(() => {
    if (!user || !enabled) return undefined;
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onActivity));
  }, [user, enabled, onActivity]);

  // Idle checker (only while enabled)
  useEffect(() => {
    if (!user || !enabled) return undefined;
    lastActivityRef.current = Date.now();
    const warnAfterMs = testOverrideMs() || Math.max(idleMs - COUNTDOWN_SEC * 1000, 5000);
    const countdownStart = Number(typeof window !== 'undefined' && window.__SESSION_COUNTDOWN_SEC__)
      || Math.min(COUNTDOWN_SEC, Math.max(5, Math.round(idleMs / 1000) - COUNTDOWN_SEC)) || COUNTDOWN_SEC;
    const id = setInterval(() => {
      if (!showWarningRef.current && Date.now() - lastActivityRef.current >= warnAfterMs) {
        setCountdown(countdownStart);
        showWarningRef.current = true;
        setShowWarning(true);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [user, enabled, idleMs]);

  // Countdown while the warning is shown
  useEffect(() => {
    if (!showWarning) return undefined;
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(id); logout(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [showWarning, logout]);

  const handleStay = async () => {
    await refreshSession();
    lastActivityRef.current = Date.now();
    showWarningRef.current = false;
    setShowWarning(false);
  };

  if (!user || !enabled) return null;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" data-testid="session-timeout-dialog">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <Clock className="h-6 w-6 text-amber-600" />
          </div>
          <DialogTitle className="text-center">Still there?</DialogTitle>
          <DialogDescription className="text-center">
            You've been inactive for a while. For your security you'll be signed out in{' '}
            <span className="font-semibold text-amber-600" data-testid="session-countdown">{countdown}</span>{' '}
            seconds.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center gap-2">
          <Button variant="outline" onClick={logout} data-testid="session-signout-btn">
            Sign out
          </Button>
          <Button onClick={handleStay} data-testid="stay-signed-in-btn">
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionTimeout;
